import { MarginWrapper } from '@travelnest/merlin.spacer';
import * as React from 'react';
import { ICommissionBreakdownObject } from '../../../data/entities/commission-breakdown';
import { IEventV2 } from '../../../data/entities/event';
import { IBookingBreakdown, IBookingV2 } from '../../../data/entities/event/booking';
import { IAppliedPromotion } from '../../../data/entities/promotions';
import { currencySymbolForCurrencyString } from '../../helpers/currency_helpers';
import { calculateOwnerTotal, displayVersionOfRestictionName, TaxSubType } from '../../pages/property/bookings/details/helpers/helpers';
import { ContentRow } from '../content-row/ContentRow';
import { FormInput, FormLabel, FormRow } from '../form';
import { HelpLink } from '../help-link/HelpLink';
import { Content } from '../page';
import { Table, Tbody, Td, Th, Thead, Tr } from '../table';
import { formatCurrencyField } from './helpers';
import { OwnerFeeBreakdown } from './OwnerFeeBreakdown';

interface IBookingBreakdownProps {
	event: IEventV2;
	commissionBreakdown: ICommissionBreakdownObject;
}

export const OwnerFinanceBreakdown: React.FC<IBookingBreakdownProps> = (props) => {

	const booking = props.event.booking || {} as IBookingV2;

	const breakdown = booking.value ? booking.value.breakdown : {} as IBookingBreakdown;
	const currencySymbol = currencySymbolForCurrencyString(booking.value.currency);

	const appliedPromotions = booking.owner_promotional_discounts || [];
	const totalPromotionsAmount = appliedPromotions.reduce((partialSum: number, curr: IAppliedPromotion) => partialSum + curr.discount_amount!, 0);

	const ownerRent = breakdown.owner?.rent_value || 0;
	const discountedOwnerRent = breakdown.owner?.discounted_owner_rent_value || ownerRent;
	const ownerPaymentValue = breakdown.owner?.payment_value || 0;

	const extrasTotal = breakdown.owner?.extras?.total || 0;

	const touristTax = breakdown.owner?.converted_tourist_tax || 0;
	const touristTaxType = breakdown.owner?.tourist_tax_type as TaxSubType;
	const chargedTouristTax = touristTaxType === 'COLLECTED_ON_TOP_OF_RENT' ? touristTax : 0;

	// stripping out dependency on the marketing prefs service means we can't refer to the discount type in direct discounts
	// for now we make a best guess - if the discount amount is 1.0 or less, it's probably a percentage.
	const bestGuessDiscountType = (amount: number = 0) => amount <= 1 ? 'percentage' : 'fixed';

	const renderAppliedDiscounts = (promotionsArr: IAppliedPromotion[]) => {
		return (promotionsArr || []).map((discount) => {
			const type = bestGuessDiscountType(discount.discount_amount);
			return <Tr>
				<Td className="booking-breakdown__table-cell--slight-wide">
					<MarginWrapper>
						{(discount.description || discount.internal_reference) + ' - ' + props.event.source}
					</MarginWrapper>
				</Td>
				{(type === 'percentage') &&
					<Td className="booking-breakdown__table-cell--narrow">
						<FormInput value={discount.discount_amount * 100 + '%'} readOnly />
					</Td>}
				<Td className="booking-breakdown__table-cell--narrow">
					<FormInput value={'-' + currencySymbol + discount.discount_amount} readOnly />
				</Td>
			</Tr>;
		});
	};

	const renderBookingBreakdownExtraRows = () => {
		if (breakdown.owner && breakdown.owner.extras && breakdown.owner.extras.items) {
			return breakdown.owner.extras.items.map((item) =>
				<Tr>
					<Tr>
						<Td>{displayVersionOfRestictionName(item.name)}</Td>
						<Td>
							<FormInput value={currencySymbol + (item.amount).toFixed(2)} readOnly={true} />
						</Td>
					</Tr>
				</Tr>);
		}

		return null;
	};

	return (
		<Content type="mid">
			<h2>Booking breakdown</h2>
			<React.Fragment>

				{/* Booking breakdown table */}
				<Table className="booking-breakdown-internal">
					<Thead>
						<Tr>
							<Th>Item</Th>
							<Th>Value</Th>
						</Tr>
					</Thead>
					<Tbody>
						<Tr>
							<Td className="booking-breakdown__table-cell--very-wide">Rent</Td>
							<Td>
								<FormInput value={formatCurrencyField(currencySymbol, ownerRent)} readOnly />
							</Td>
						</Tr>
					</Tbody>
				</Table>
				<Table>
					<Tbody>
						{renderAppliedDiscounts(appliedPromotions)}
					</Tbody>
				</Table>
				<Table>
					<Tbody>
						{appliedPromotions.length !== 0 &&
							<Tr>
								<Td className="booking-breakdown__table-cell--very-wide">Total discount</Td>
								<Td>
									<FormInput value={formatCurrencyField(currencySymbol, totalPromotionsAmount)} readOnly />
								</Td>
							</Tr>}
						{appliedPromotions.length !== 0 &&
							<Tr>
								<Td className="booking-breakdown__table-cell--very-wide">Discounted rent</Td>
								<Td>
									<FormInput value={formatCurrencyField(currencySymbol, discountedOwnerRent)} readOnly />
								</Td>
							</Tr>}
						{chargedTouristTax !== 0 &&
						<Tr>
							<Td className="booking-breakdown__table-cell--very-wide">Tourist tax</Td>
							<Td>
								<FormInput value={formatCurrencyField(currencySymbol, chargedTouristTax)} readOnly />
							</Td>
						</Tr>
						}
						{renderBookingBreakdownExtraRows()}
						<Tr>
							<Td className="booking-breakdown__table-cell--right-align">Total</Td>
							<Td className="booking-breakdown__table-cell--slight-wide ">
								<FormInput
									value={formatCurrencyField(currencySymbol, calculateOwnerTotal(discountedOwnerRent, extrasTotal, chargedTouristTax))}
									readOnly={true}
								/>
							</Td>
						</Tr>
					</Tbody>
				</Table>

				{/* Breakdown of Commission (displayed as Fees to customers) */}
				<OwnerFeeBreakdown
					breakdown={breakdown}
					currencySymbol={currencySymbol}
					appliedPromotions={appliedPromotions}
					commissionBreakdown={props.commissionBreakdown}
				/>

				{/* Payment Section */}
				<div className="booking-list__section">
					<h2>Payment</h2>
					<FormRow>
						<FormLabel htmlFor="owner_payments">Income after deductions</FormLabel>
						<FormInput value={formatCurrencyField(currencySymbol, ownerPaymentValue)} readOnly={true} />
					</FormRow>
					<ContentRow>
						<HelpLink to="https://travelnest.freshdesk.com/support/solutions/articles/101000362570-payments">
							When will I get paid for this booking?
						</HelpLink>
					</ContentRow>
				</div>
			</React.Fragment>
		</Content >
	);
};
